<!-- eslint-disable vue/no-mutating-props - Needed for Formulate integration -->

<template>
  <div :class="$style.findandselect" style="flex: 1">
    <VDropdown :shown="showDropdown" theme="findandselect">
      <div class="flex items-center gap-2">
        <input
          v-bind="context.attributes"
          :id="context.id"
          ref="inputfield"
          v-model="searchText"
          type="search"
          :placeholder="context.placeholder"
          :name="context.node.name"
          :class="context.classes.input"
          :disabled="context.disabled"
          @keydown.enter.prevent="set"
          @keydown.down.prevent="increment"
          @keydown.up.prevent="decrement"
          @input="updateSearch"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>

      <template #popper="{}">
        <LoadingDots v-if="isLoading" class="text-center my-4" />
        <div v-if="!isLoading">
          <ul ref="dropdown" :class="$style.dropdown">
            <li
              v-for="(option, index) in filteredOptions"
              :key="option.item.key || option.item.value"
              :data-value="option.item.value"
              :data-key="option.item.key"
              :title="option.item.title"
              :data-is-selected="context._value === option.item.value"
              :data-is-highlighted="
                selection && selection.item.value === option.item.value
              "
              :class="context.itemClass"
              @mouseenter="selectedIndex = index"
              @mousedown="onMousedown"
              @click="set"
            >
              <component
                :is="context.itemComponent"
                v-if="context.itemComponent"
                :item="option.item"
              />
              <span v-else-if="!option.item.columns">{{
                option.item.label
              }}</span>
              <div
                v-for="(column, columnIndex) in option.item.columns"
                :key="columnIndex"
                :class="column.class"
              >
                {{ column.text }}
              </div>
            </li>
          </ul>
          <div v-if="showCount" class="text-subtle mx-4 my-2">
            <small>{{
              $t('formulate:findAndSelect.resultCount', {
                count: filteredOptions.length,
              })
            }}</small>
          </div>
          <div
            v-if="!filteredOptions.length && !context.hideNoResult"
            class="text-center"
            :class="$style.noResultDropdown"
          >
            <strong>
              {{ $t('formulate:findAndSelect.noResults') }}
            </strong>
            <p v-if="context.noResultMessage">
              {{ context.noResultMessage }}
            </p>
          </div>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
import { createFuzzySearch } from '@cutr/kanna';

export default {
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      searchText: '',
      selectedIndex: 0,
      performance: 0,
      hasFocus: false,
    };
  },
  computed: {
    showDropdown() {
      if (this.context.showOnFocus && this.hasFocus) return true;
      if (this.context.disabled) return false;
      if (!this.hasFocus) return false;
      if (!this.context.showDropdownByDefault && !this.searchText) return false;
      if (
        !this.showCount &&
        !this.isLoading &&
        !this.filteredOptions.length &&
        this.context.hideNoResult
      )
        return false;
      return true;
    },
    showCount() {
      return Boolean(this.context.showCount);
    },
    model() {
      return this.context._value;
    },
    selection() {
      if (this.filteredOptions[this.selectedIndex]) {
        return this.filteredOptions[this.selectedIndex];
      }
      return false;
    },
    filteredOptions() {
      if (this.context.filteredOptions)
        return Object.freeze(
          this.context.filteredOptions.map((item) => ({ item })) || []
        );
      if (!Array.isArray(this.context.options)) return [];
      if (!this.searchText)
        return Object.freeze(this.context.options.map((item) => ({ item })));

      const searchKeys = this.context.searchKeys || [];
      const fuzzySearch = createFuzzySearch(this.context.options, {
        getText: (opt) => searchKeys.map((k) => opt.searchData?.[k] ?? ''),
      });
      let results = fuzzySearch(this.searchText.trim());
      if (this.context.limit) results = results.slice(0, this.context.limit);

      if (
        this.context.addOther &&
        !this.context.options.find(
          (o) => o.value.toLowerCase() === this.searchText.trim().toLowerCase()
        )
      ) {
        results.unshift({
          item: {
            value: this.searchText,
            label: this.searchText,
            isAddOption: true,
          },
        });
      }

      return Object.freeze(results);
    },

    isLoading() {
      return this.context.loading || false;
    },
  },
  watch: {
    searchText(val, oldVal) {
      if (oldVal && !val) {
        this.context.onClear?.();
      }
    },
  },
  created() {
    const val = this.context._value;
    const option = (this.context.options || []).find((o) => o.value === val);
    this.searchText = option?.label ?? '';

    if (!option) {
      this.searchText = val;
    }
  },
  methods: {
    clear(e) {
      e.preventDefault();
      this.context.node.input(undefined);
      this.searchText = '';
      this.context.onClear?.();
    },
    onMousedown(e) {
      e.preventDefault();
      this.context.node.input(this.selection.item.value);
    },
    onFocus() {
      this.hasFocus = true;
    },
    onBlur(e) {
      // this.hasFocus = false;
      this.$nextTick(() => {
        this.hasFocus = false;
      });
      if (this.context['select-on-blur']) {
        this.set();
      }
      this.context.handlers.blur(e);
    },
    updateSearch() {
      this.context.node.input(undefined);
      this.selectedIndex = 0;

      if (this.$refs['dropdown']) {
        this.$refs['dropdown'].scrollTop = 0;
      }
      this.context.onSearchInput?.(this.searchText);
    },
    set() {
      this.context.node.input(this.selection.item.value);
      this.context.onSet?.(this.selection.item);
      this.searchText = this.selection.item.label;
      this.$refs['inputfield'].blur();
    },
    increment() {
      const length = this.filteredOptions.length;
      if (this.selectedIndex + 1 < length) {
        this.selectedIndex++;
      } else {
        this.selectedIndex = 0;
      }
    },
    decrement() {
      const length = this.filteredOptions.length;
      if (this.selectedIndex - 1 >= 0) {
        this.selectedIndex--;
      } else {
        this.selectedIndex = length - 1;
      }
    },
  },
};
</script>

<style module>
.noResultDropdown {
  padding: 2em 2em 1.5em;
}

.findandselect {
  :global(.v-popper) {
    width: 100%;
  }
}

.dropdown {
  padding: 0;
  max-height: 400px;
  list-style-type: none;
  overflow: auto;
}

.dropdown li {
  padding: 0.5em 1em;
  margin: 0;

  &:hover {
    cursor: pointer;
  }
}

.dropdown li:hover,
.dropdown li[data-is-highlighted='true'] {
  background-color: rgb(0 0 0 / 5%);
}

.dropdown li[data-is-selected='true'] {
  background-color: var(--amber-600);
}
</style>
